import React, { useState, useEffect } from "react";
import { X, Check } from "lucide-react";

const CookiePreferencesModal = ({ isOpen, onClose, onSave, initialSettings }) => {
  const [settings, setSettings] = useState({
    strictlyNecessary: true,
    analytics: false,
    marketing: false,
    ...initialSettings
  });

  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimateIn(true));
      });
    } else {
      setAnimateIn(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleSetting = (key) => {
    if (key === 'strictlyNecessary') return; // Cannot toggle off
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div 
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
      style={{ opacity: animateIn ? 1 : 0 }}
    >
      <div 
        className="w-full max-w-[500px] bg-[#0a0a0a] border border-white/10 rounded-[32px] shadow-2xl overflow-hidden transition-all duration-300"
        style={{ 
          transform: animateIn ? "scale(1) translateY(0)" : "scale(0.95) translateY(10px)",
          opacity: animateIn ? 1 : 0 
        }}
      >
        <div className="p-8">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-xl font-semibold text-white tracking-tight">Cookie Preferences Center</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors cursor-pointer">
              <X size={20} />
            </button>
          </div>

          <p className="text-gray-400 text-[13px] leading-relaxed mb-8">
            Using websites and apps involves storing and retrieving information from your device, including cookies and other identifiers, which can be shared with third parties, for various activities. We provide a simple tool below allowing you to tailor your choices as you deem fit. You can change your consent at any time. <a href="/cookie-policy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline underline-offset-4 font-medium transition-colors">Learn more</a>.
          </p>

          <div className="space-y-6">
            {/* Strictly Necessary */}
            <div className="flex items-start gap-4">
              <div className="mt-1">
                <div className="w-5 h-5 rounded border border-white/20 bg-white/10 flex items-center justify-center opacity-50">
                  <Check size={14} className="text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-[14px] font-medium text-white mb-1">Strictly Necessary Cookies (always active)</h4>
                <p className="text-[12px] text-gray-500 leading-relaxed">
                  These cookies are essential for the site to function and cannot be toggled off. They assist with security, user authentication, customer support, etc.
                </p>
              </div>
            </div>

            {/* Analytics */}
            <label className="flex items-start gap-4 cursor-pointer group">
              <div className="mt-1 relative">
                <input 
                  type="checkbox" 
                  className="sr-only" 
                  checked={settings.analytics}
                  onChange={() => toggleSetting('analytics')}
                />
                <div className={`w-5 h-5 rounded border transition-all ${settings.analytics ? 'bg-white border-white' : 'border-white/20 bg-transparent group-hover:border-white/40'}`}>
                  {settings.analytics && <Check size={14} className="text-black" />}
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-[14px] font-medium text-white mb-1 group-hover:text-white/90">Analytics Cookies</h4>
                <p className="text-[12px] text-gray-500 leading-relaxed">
                  These cookies help us understand how visitors interact with our site. They allow us to measure traffic and improve site performance.
                </p>
              </div>
            </label>

            {/* Marketing */}
            <label className="flex items-start gap-4 cursor-pointer group">
              <div className="mt-1 relative">
                <input 
                  type="checkbox" 
                  className="sr-only" 
                  checked={settings.marketing}
                  onChange={() => toggleSetting('marketing')}
                />
                <div className={`w-5 h-5 rounded border transition-all ${settings.marketing ? 'bg-white border-white' : 'border-white/20 bg-transparent group-hover:border-white/40'}`}>
                  {settings.marketing && <Check size={14} className="text-black" />}
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-[14px] font-medium text-white mb-1 group-hover:text-white/90">Marketing Performance Cookies</h4>
                <p className="text-[12px] text-gray-500 leading-relaxed">
                  These cookies help us measure the effectiveness of our marketing campaigns.
                </p>
              </div>
            </label>
          </div>

          <div className="mt-10 flex justify-center">
            <button
              onClick={() => onSave(settings)}
              className="px-10 py-2 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-500 transition-colors cursor-pointer text-[13px] shadow-lg shadow-blue-500/20"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferencesModal;
